import { AI_BUILDER_MISSING_SLOT } from '../consts';
import {
  type AiBuilderProgressStep,
  type AiBuilderUsageInfo,
} from '../stores/types';

import { type AiBuilderChatCopy } from './aiBuilderChatCopy';
import {
  type ServerAiBuilderChatAssistantActionDto,
  type ServerAiBuilderChatChipsEventDto,
  type ServerAiBuilderChatConfirmRequiredEventDto,
  type ServerAiBuilderChatErrorEventDto,
  type ServerAiBuilderChatEventDto,
  type ServerAiBuilderChatNoticeEventDto,
  type ServerAiBuilderChatProgressEventDto,
  type ServerAiBuilderChatQuestionEventDto,
  type ServerAiBuilderChatQuestionRefDto,
  type ServerAiBuilderChatQuestionsChangeType,
  type ServerAiBuilderChatQuestionSnapshotDto,
  type ServerAiBuilderChatQuestionsUpdatedEventDto,
  type ServerAiBuilderChatRespondEventDto,
  type ServerAiBuilderChatRespondResponseType,
  type ServerAiBuilderChatTextDeltaEventDto,
  type ServerAiBuilderChatThinkingDeltaEventDto,
  type ServerAiBuilderChatTurnMetadataDto,
  type ServerAiBuilderChatTurnStartedEventDto,
  type ServerAiBuilderChatUsageInfoDto,
} from './serverAiBuilderChatDtos';
import {
  getServerAiBuilderChatErrorMessage,
  getServerAiBuilderChatNoticeMessage,
} from './serverAiBuilderChatErrorMessages';
import {
  type AiBuilderChatEvent,
  type AiBuilderChatOrchestrationEvent,
  type AiBuilderChatQuestionSnapshot,
  type AiBuilderChatStateEvent,
} from './types';

export type ServerAiBuilderChatMappedEvent = AiBuilderChatOrchestrationEvent;

export type ServerAiBuilderChatEventMapperState = {
  progressMessage?: {
    id: string;
    title: string;
    steps: AiBuilderProgressStep[];
    source: 'pending' | 'server';
    turnIdentity?: string | number;
    /** 진행 중 카드에 흘려보내는 누적 사고 과정(ephemeral). 답변 시작 시 제거. */
    thinkingText?: string;
  };
  streamingMessage?: {
    id: string;
    text: string;
  };
  questionUpdate?: {
    changedCount: number;
    changeType: ServerAiBuilderChatQuestionsChangeType;
  };
  visibleTurnResponse?: boolean;
};

export type MapServerAiBuilderChatEventOptions = {
  copy: AiBuilderChatCopy;
  requestId: number;
  state?: ServerAiBuilderChatEventMapperState;
};

export type ServerAiBuilderChatEventMappingResult = {
  events: ServerAiBuilderChatMappedEvent[];
  state: ServerAiBuilderChatEventMapperState;
};

type AiBuilderAppendEvent = Extract<AiBuilderChatEvent, { type: 'append' }>;
type AiBuilderMappedMessage = AiBuilderAppendEvent['message'];
type AiBuilderMappedProgressMessage = Extract<
  AiBuilderMappedMessage,
  { kind: 'progress' }
>;
type AiBuilderMappedAssistantMessage = Extract<
  AiBuilderMappedMessage,
  { kind: 'assistant' }
>;
type AiBuilderMappedSystemMessage = Extract<
  AiBuilderMappedMessage,
  { kind: 'system' }
>;
type AiBuilderMappedChoiceMessage = Extract<
  AiBuilderMappedMessage,
  { kind: 'choice' }
>;
type AiBuilderMappedConfirmMessage = Extract<
  AiBuilderMappedMessage,
  { kind: 'confirm' }
>;
type AiBuilderMappedChoiceOption =
  AiBuilderMappedChoiceMessage['options'][number];
type AiBuilderTerminalProgressStatus = Extract<
  AiBuilderMappedProgressMessage['status'],
  'complete' | 'stopped'
>;

export const createServerAiBuilderChatEventMapperState =
  (): ServerAiBuilderChatEventMapperState => ({});

export const mapServerAiBuilderChatEvent = (
  event: ServerAiBuilderChatEventDto,
  options: MapServerAiBuilderChatEventOptions,
): ServerAiBuilderChatEventMappingResult => {
  const state = options.state ?? createServerAiBuilderChatEventMapperState();

  switch (event.type) {
    case 'turn_started':
      return mapTurnStartedEvent(event, options.requestId, state, options.copy);
    case 'progress':
      return mapProgressEvent(event, options.requestId, state, options.copy);
    case 'thinking_delta':
      return mapThinkingDeltaEvent(
        event,
        options.requestId,
        state,
        options.copy,
      );
    case 'text_delta':
      return mapTextDeltaEvent(event, options.requestId, state, options.copy);
    case 'notice':
      return {
        events: [
          createSystemNoticeEvent(event, options.requestId, options.copy),
        ],
        state,
      };
    case 'respond':
      return mapRespondEvent(event, options.requestId, state, options.copy);
    case 'error':
      return mapErrorEvent(event, options.requestId, state, options.copy);
    case 'question':
      return mapVisibleResponseEvent(
        mapQuestionEvent(event, options.requestId, options.copy),
        state,
      );
    case 'chips':
      return mapVisibleResponseEvent(
        mapChipsEvent(event, options.requestId),
        state,
      );
    case 'confirm_required':
      return mapVisibleResponseEvent(
        mapConfirmRequiredEvent(event, options.requestId, options.copy),
        state,
      );
    case 'questions_updated':
      return mapQuestionsUpdatedEvent(event, options.requestId, state);
    default:
      return {
        events: [],
        state,
      };
  }
};

export const mapServerAiBuilderChatEvents = (
  events: ServerAiBuilderChatEventDto[],
  options: MapServerAiBuilderChatEventOptions,
): ServerAiBuilderChatEventMappingResult => {
  const mappedEvents: ServerAiBuilderChatMappedEvent[] = [];
  let state = options.state ?? createServerAiBuilderChatEventMapperState();

  events.forEach(event => {
    const result = mapServerAiBuilderChatEvent(event, {
      requestId: options.requestId,
      copy: options.copy,
      state,
    });

    mappedEvents.push(...result.events);
    state = result.state;
  });

  return {
    events: mappedEvents,
    state,
  };
};

const mapTurnStartedEvent = (
  event: ServerAiBuilderChatTurnStartedEventDto,
  requestId: number,
  state: ServerAiBuilderChatEventMapperState,
  copy: AiBuilderChatCopy,
): ServerAiBuilderChatEventMappingResult => {
  const progressMessage = createProgressMessage({
    id: createServerAiBuilderChatMessageId({
      event,
      requestId,
      suffix: 'progress',
    }),
    title: copy.serverEvent.pendingProgressTitle,
    status: 'running',
    steps: [],
  });

  return {
    events: [
      createSessionIdEvent(event.session_id),
      {
        type: 'append',
        message: progressMessage,
      },
    ],
    state: {
      ...state,
      progressMessage: {
        id: progressMessage.id,
        title: progressMessage.title,
        steps: progressMessage.steps,
        source: 'pending',
        turnIdentity: getTurnIdentity(event),
      },
    },
  };
};

/**
 * progress 문구 해석 — message_code 가 등록돼 있으면 FE 카피, 아니면 서버
 * message verbatim (RFC #412 code 계약, 구서버/미등록 code 하위호환).
 */
const resolveServerProgressTitle = (
  event: ServerAiBuilderChatProgressEventDto,
  copy: AiBuilderChatCopy,
): string => {
  const code = event.message_code?.trim();
  if (!code) return event.message;

  const localized =
    copy.serverProgress.byCode[code as keyof typeof copy.serverProgress.byCode];

  return localized ?? event.message;
};

/**
 * Deterministic assistant copy는 code+params가 완전할 때만 FE locale로 렌더한다.
 * 불완전·미지 code는 아래 호출부에서 legacy server text/message로 안전하게 내린다.
 */
const resolveServerAssistantCopy = (
  code: string | null | undefined,
  params: Record<string, unknown> | null | undefined,
  copy: AiBuilderChatCopy,
): string | null => {
  const values = params ?? {};
  const number = (key: string) =>
    typeof values[key] === 'number' && Number.isInteger(values[key])
      ? values[key]
      : null;
  // 번역 대상이 아닌 고유명사(파일 제목)만 문자열 param 으로 받는다 — 서버가
  // 로케일 산문을 params 로 흘리는 것을 막기 위해 용도를 좁게 유지한다.
  const text = (key: string) =>
    typeof values[key] === 'string' && values[key].trim() ? values[key] : null;

  switch (code) {
    case null:
    case undefined:
      return null;
    case 'TERMINAL_COMPLETED':
      return copy.serverAssistant.terminalCompleted;
    case 'TERMINAL_CLARIFY':
      return copy.serverAssistant.terminalClarify;
    case 'TERMINAL_UNDO_COMPLETED':
      return copy.serverAssistant.terminalUndoCompleted;
    case 'TERMINAL_CONFIRM_CANCELLED':
      return copy.serverAssistant.terminalConfirmCancelled;
    case 'TERMINAL_CONFIRM_STALE':
      return copy.serverAssistant.terminalConfirmStale;
    case 'QUESTION_LIMIT_ADJUSTED': {
      const limit = number('limit');
      const currentCount = number('current_count');
      const adjustedCount = number('adjusted_count');
      return limit === null || currentCount === null || adjustedCount === null
        ? null
        : copy.serverAssistant.questionLimitAdjusted({
            limit,
            currentCount,
            adjustedCount,
          });
    }
    case 'QUESTION_LIMIT_REACHED': {
      const limit = number('limit');
      const currentCount = number('current_count');
      return limit === null || currentCount === null
        ? null
        : copy.serverAssistant.questionLimitReached({ limit, currentCount });
    }
    case 'TARGET_OUT_OF_RANGE_REORDER': {
      const totalCount = number('total_count');
      const invalidNumbers = Array.isArray(values.invalid_numbers)
        ? values.invalid_numbers.filter(
            (value): value is number => typeof value === 'number',
          )
        : [];
      return totalCount === null || invalidNumbers.length === 0
        ? null
        : copy.serverAssistant.targetOutOfRangeReorder({
            numbers: invalidNumbers.join(', '),
            totalCount,
          });
    }
    case 'TARGET_REQUIRED_REORDER':
      return copy.serverSemantic.targetRequiredReorder;
    case 'QUESTION_WORKSHEET_IMPORT_WITH_ANSWERS': {
      const title = text('title');
      const questionCount = number('question_count');
      return title === null || questionCount === null
        ? null
        : copy.serverSemantic.question.worksheetImportWithAnswers({
            title,
            questionCount,
          });
    }
    case 'QUESTION_WORKSHEET_IMPORT_WITHOUT_ANSWERS': {
      const title = text('title');
      const questionCount = number('question_count');
      return title === null || questionCount === null
        ? null
        : copy.serverSemantic.question.worksheetImportWithoutAnswers({
            title,
            questionCount,
          });
    }
    case 'SOURCE_YOUTUBE_INVALID_URL':
      return copy.serverSemantic.source.youtubeInvalidUrl;
    case 'SOURCE_YOUTUBE_UNAVAILABLE':
      return copy.serverSemantic.source.youtubeUnavailable;
    case 'SOURCE_YOUTUBE_CAPTIONS_UNAVAILABLE':
      return copy.serverSemantic.source.youtubeCaptionsUnavailable;
    case 'SOURCE_WEBSITE_INVALID_URL':
      return copy.serverSemantic.source.websiteInvalidUrl;
    case 'SOURCE_WEBSITE_UNREADABLE':
      return copy.serverSemantic.source.websiteUnreadable;
    case 'SOURCE_WEBSITE_TOO_LARGE':
      return copy.serverSemantic.source.websiteTooLarge;
    case 'SOURCE_CHECK_FAILED':
      return copy.serverSemantic.source.checkFailed;
    case 'COMPLETION_GENERATED': {
      const createdCount = number('created_count');
      return createdCount === null
        ? null
        : copy.serverSemantic.completion.generated(createdCount);
    }
    case 'COMPLETION_IMPORTED': {
      const importedCount = number('imported_count');
      const skippedCount = number('skipped_count');
      if (importedCount === null || skippedCount === null) return null;
      return skippedCount > 0
        ? copy.serverSemantic.completion.importedPartial(
            importedCount,
            skippedCount,
          )
        : copy.serverSemantic.completion.imported(importedCount);
    }
    case 'COMPLETION_IMPORT_EMPTY':
      return copy.serverSemantic.completion.importEmpty;
    case 'COMPLETION_REGENERATED': {
      const changedCount = number('changed_count');
      return changedCount === null
        ? null
        : copy.serverSemantic.completion.regenerated(changedCount);
    }
    case 'COMPLETION_REPLACED': {
      const changedCount = number('changed_count');
      return changedCount === null
        ? null
        : copy.serverSemantic.completion.replaced(changedCount);
    }
    case 'COMPLETION_HINTS_ADDED': {
      const changedCount = number('changed_count');
      return changedCount === null
        ? null
        : copy.serverSemantic.completion.hintsAdded(changedCount);
    }
    case 'COMPLETION_EDITED': {
      const changedCount = number('changed_count');
      const failedCount = number('failed_count');
      if (changedCount === null) return null;
      return failedCount !== null && failedCount > 0
        ? copy.serverSemantic.completion.editedPartial(
            changedCount,
            failedCount,
          )
        : copy.serverSemantic.completion.edited(changedCount);
    }
    case 'COMPLETION_REORDERED':
      return copy.serverSemantic.completion.reordered;
    case 'COMPLETION_NO_QUESTIONS':
      return copy.serverSemantic.completion.noQuestions;
    case 'COMPLETION_QUESTION_NOT_FOUND':
      return copy.serverSemantic.completion.questionNotFound;
    case 'COMPLETION_NO_CHANGES':
      return copy.serverSemantic.completion.noChanges;
    case 'COMPLETION_INVALID_ORDER':
      return copy.serverSemantic.completion.invalidOrder;
    case 'COMPLETION_NOT_ENOUGH_QUESTIONS':
      return copy.serverSemantic.completion.notEnoughQuestions;
    case 'COMPLETION_EDIT_FAILED':
      return copy.serverSemantic.completion.editFailed;
    case 'COMPLETION_REGENERATION_FAILED':
      return copy.serverSemantic.completion.regenerationFailed;
    case 'COMPLETION_HINT_FAILED':
      return copy.serverSemantic.completion.hintFailed;
    case 'COMPLETION_FAILED':
      return copy.serverError.fallback;
    default:
      return null;
  }
};

const mapProgressEvent = (
  event: ServerAiBuilderChatProgressEventDto,
  requestId: number,
  state: ServerAiBuilderChatEventMapperState,
  copy: AiBuilderChatCopy,
): ServerAiBuilderChatEventMappingResult => {
  const previousProgress = isSameProgressTurn(state.progressMessage, event)
    ? state.progressMessage
    : null;
  const messageId =
    previousProgress?.id ??
    createServerAiBuilderChatMessageId({
      event,
      requestId,
      suffix: 'progress',
    });
  const title = resolveServerProgressTitle(event, copy);
  const steps = createProgressSteps(
    previousProgress?.steps ?? [],
    event,
    title,
  );
  // 스텝 갱신이 진행 중인 사고 과정을 지우지 않도록 이어서 유지한다.
  const thinkingText = previousProgress?.thinkingText;
  const message = createProgressMessage({
    id: messageId,
    title,
    status: 'running',
    steps,
    thinkingText,
  });
  const mappedEvent: AiBuilderChatEvent = previousProgress
    ? {
        type: 'replace',
        messageId,
        message,
      }
    : {
        type: 'append',
        message,
      };

  return {
    events: [mappedEvent],
    state: {
      ...state,
      progressMessage: {
        id: messageId,
        title,
        steps,
        source: 'server',
        turnIdentity: getTurnIdentity(event),
        ...(thinkingText !== undefined ? { thinkingText } : {}),
      },
    },
  };
};

const mapTextDeltaEvent = (
  event: ServerAiBuilderChatTextDeltaEventDto,
  requestId: number,
  state: ServerAiBuilderChatEventMapperState,
  copy: AiBuilderChatCopy,
): ServerAiBuilderChatEventMappingResult => {
  const pendingProgressMessageId =
    state.progressMessage?.source === 'pending'
      ? state.progressMessage.id
      : null;
  const messageId =
    state.streamingMessage?.id ??
    createServerAiBuilderChatMessageId({
      event,
      requestId,
      suffix: 'assistant-response',
    });
  const previousText = state.streamingMessage?.text ?? '';
  const nextText = `${previousText}${event.delta}`;
  const message = createAssistantMessage({
    id: messageId,
    text: nextText,
    copy,
  });
  const shouldReplaceMessage =
    Boolean(state.streamingMessage) || Boolean(pendingProgressMessageId);
  const mappedEvent: AiBuilderChatEvent = shouldReplaceMessage
    ? {
        type: 'replace',
        messageId:
          state.streamingMessage?.id ?? pendingProgressMessageId ?? messageId,
        message,
      }
    : {
        type: 'append',
        message,
      };

  // 답변 본문이 시작됐다. pending progress 는 위에서 assistant 로 교체되며
  // 사라지지만, 남아 있는 server progress 카드에 사고 과정이 붙어 있으면 답변과
  // 나란히 남지 않도록 지금 벗겨낸다("답변 오면 사라짐").
  const { events: thinkingStripEvents, progressMessage: nextProgressMessage } =
    stripStreamingThinkingText(state.progressMessage);

  return {
    events: [...thinkingStripEvents, mappedEvent],
    state: {
      ...state,
      progressMessage:
        state.progressMessage?.source === 'pending'
          ? undefined
          : nextProgressMessage,
      streamingMessage: {
        id: messageId,
        text: nextText,
      },
    },
  };
};

// 진행 중 카드에서 사고 과정(thinkingText)만 제거한다. 카드 자체(제목·스텝)는
// 그대로 두고 ephemeral 한 reasoning 프로즈만 걷어낸다.
const stripStreamingThinkingText = (
  progressMessage: ServerAiBuilderChatEventMapperState['progressMessage'],
): {
  events: AiBuilderChatEvent[];
  progressMessage: ServerAiBuilderChatEventMapperState['progressMessage'];
} => {
  if (
    progressMessage?.source !== 'server' ||
    progressMessage.thinkingText === undefined
  ) {
    return { events: [], progressMessage };
  }

  const { thinkingText: _removed, ...progressWithoutThinking } =
    progressMessage;

  return {
    events: [
      {
        type: 'replace',
        messageId: progressMessage.id,
        message: createProgressMessage({
          id: progressMessage.id,
          title: progressMessage.title,
          status: 'running',
          steps: progressMessage.steps,
        }),
      },
    ],
    progressMessage: progressWithoutThinking,
  };
};

const mapThinkingDeltaEvent = (
  event: ServerAiBuilderChatThinkingDeltaEventDto,
  requestId: number,
  state: ServerAiBuilderChatEventMapperState,
  copy: AiBuilderChatCopy,
): ServerAiBuilderChatEventMappingResult => {
  const previousProgress = isSameProgressTurn(state.progressMessage, event)
    ? state.progressMessage
    : null;
  const messageId =
    previousProgress?.id ??
    createServerAiBuilderChatMessageId({
      event,
      requestId,
      suffix: 'progress',
    });
  const title =
    previousProgress?.title ?? copy.serverEvent.pendingProgressTitle;
  const steps = previousProgress?.steps ?? [];
  const source = previousProgress?.source ?? 'pending';
  const nextThinkingText = `${previousProgress?.thinkingText ?? ''}${
    event.delta
  }`;
  const message = createProgressMessage({
    id: messageId,
    title,
    status: 'running',
    steps,
    thinkingText: nextThinkingText,
  });
  const mappedEvent: AiBuilderChatEvent = previousProgress
    ? {
        type: 'replace',
        messageId,
        message,
      }
    : {
        type: 'append',
        message,
      };

  return {
    events: [mappedEvent],
    state: {
      ...state,
      progressMessage: {
        id: messageId,
        title,
        steps,
        source,
        turnIdentity: getTurnIdentity(event),
        thinkingText: nextThinkingText,
      },
    },
  };
};

const mapQuestionEvent = (
  event: ServerAiBuilderChatQuestionEventDto,
  requestId: number,
  copy: AiBuilderChatCopy,
): AiBuilderChatEvent => {
  const localizedQuestion = resolveServerAssistantCopy(
    event.copy_code,
    event.copy_params,
    copy,
  );
  // 서버 chips tool 병합(2026-07-03) — 선택지 질문도 question 이벤트 하나로 온다.
  // options 가 비어 있지 않으면 선택 UI(choice message)로 렌더한다.
  // 구조화 옵션 계약(2026-07-16) — 객체 배열이 정본, string 은 전환기 가드.
  const options = (event.options ?? [])
    .map(option => (typeof option === 'string' ? { label: option } : option))
    .filter(option => Boolean(option?.label?.trim()));
  const missingSlot = event.missing_slot ?? undefined;
  if (options.length > 0) {
    return {
      type: 'append',
      message: createChoiceMessage({
        id: createServerAiBuilderChatMessageId({
          event,
          requestId,
          suffix: 'choice',
        }),
        title: localizedQuestion ?? event.question,
        traceId: event.trace_id,
        missingSlot,
        selectionMode: getServerChipSelectionMode(event.selection_mode),
        options: options.map((option, index) => ({
          id: createServerChipOptionId(index),
          ...(option.description?.trim()
            ? { description: option.description }
            : {}),
          ...resolveServerChoiceOptionCopy({
            option,
            missingSlot,
            copyCode: event.copy_code,
            copy,
          }),
        })),
      }),
    };
  }

  return {
    type: 'append',
    message: createAssistantMessage({
      id: createServerAiBuilderChatMessageId({
        event,
        requestId,
        suffix: 'question',
      }),
      text: localizedQuestion ?? createQuestionContent(event),
      traceId: event.trace_id,
      copy,
    }),
  };
};

const mapChipsEvent = (
  event: ServerAiBuilderChatChipsEventDto,
  requestId: number,
): AiBuilderChatEvent => {
  return {
    type: 'append',
    message: createChoiceMessage({
      id: createServerAiBuilderChatMessageId({
        event,
        requestId,
        suffix: 'choice',
      }),
      title: event.prompt,
      selectionMode: getServerChipSelectionMode(event.selection_mode),
      options: event.options.map((label, index) => ({
        id: createServerChipOptionId(index),
        label,
      })),
    }),
  };
};

const getServerChipSelectionMode = (
  selectionMode: ServerAiBuilderChatChipsEventDto['selection_mode'],
): AiBuilderMappedChoiceMessage['selectionMode'] => {
  return selectionMode === 'multiple' ? 'multiple' : 'single';
};

const COUNT_COPY_CODE = 'QUESTION_SELECT_COUNT';
const COUNT_PRESET_VALUES = new Set(['5', '10', '15', '20']);

/**
 * canonical id 선지를 쓰는 슬롯 → 그 슬롯의 locale 라벨 카탈로그.
 * 슬롯이 늘어도 분기가 아니라 등록 한 줄로 끝난다.
 */
const CANONICAL_OPTION_LABELS_BY_SLOT: Record<
  string,
  (copy: AiBuilderChatCopy) => Record<string, string | undefined>
> = {
  [AI_BUILDER_MISSING_SLOT.quizTypes]: copy => copy.choice.quizTypeOptionLabels,
  [AI_BUILDER_MISSING_SLOT.worksheetImport]: copy =>
    copy.choice.worksheetImportOptionLabels,
};

/**
 * 서버 option 을 표시 라벨(+canonical value)로 해석한다 (label→value 계약).
 * canonical id 슬롯(quiz_types·worksheet_import)은 FE locale 카피로 렌더하고,
 * 그 외(자유 텍스트·미지 id·레거시 라벨)는 verbatim 표시로 폴백한다 —
 * resolveServerConfirmCopy 의 code 계약과 같은 패턴.
 * canonical lookup key 는 value ?? label — 구조화 계약(2026-07-16)에서
 * 서버 정규화가 label 에 canonical id 를 실어 보내는 경우(value=null)와
 * 배선이 value 를 채워 보내는 경우를 모두 흡수한다.
 */
const resolveServerChoiceOptionCopy = ({
  option,
  missingSlot,
  copyCode,
  copy,
}: {
  option: { label: string; value?: string | null };
  missingSlot?: string;
  copyCode?: string | null;
  copy: AiBuilderChatCopy;
}): { label: string; value?: string } => {
  const canonicalValue = option.value ?? undefined;
  const verbatim = canonicalValue
    ? { label: option.label, value: canonicalValue }
    : { label: option.label };

  if (
    missingSlot === AI_BUILDER_MISSING_SLOT.count &&
    copyCode === COUNT_COPY_CODE &&
    canonicalValue !== undefined &&
    COUNT_PRESET_VALUES.has(canonicalValue)
  ) {
    return {
      label: copy.choice.countOption(Number(canonicalValue)),
      value: canonicalValue,
    };
  }

  const labels = missingSlot
    ? CANONICAL_OPTION_LABELS_BY_SLOT[missingSlot]?.(copy)
    : undefined;
  if (!labels) return verbatim;

  const lookupKey = canonicalValue ?? option.label;
  const localizedLabel = labels[lookupKey];
  if (!localizedLabel) return verbatim;

  return { label: localizedLabel, value: lookupKey };
};

/**
 * confirm_required 의 code+params 로 FE 카피를 해석한다 (RFC #412 code 계약).
 * code 미등록·params 불충분이면 null — 서버 message/label verbatim 폴백.
 * 확인 응답은 decision(approve/cancel)으로 제출되므로 라벨 지역화가
 * 왕복(서버 파싱)에 영향을 주지 않는다.
 */
const resolveServerConfirmCopy = (
  event: ServerAiBuilderChatConfirmRequiredEventDto,
  copy: AiBuilderChatCopy,
): { title: string; approveLabel: string } | null => {
  const params = event.params ?? {};

  switch (event.code) {
    case 'CONFIRM_DELETE_QUESTIONS': {
      const numbers = Array.isArray(params.question_numbers)
        ? params.question_numbers.filter(
            (value): value is number => typeof value === 'number',
          )
        : [];
      const count =
        typeof params.count === 'number' ? params.count : numbers.length;

      if (count === 1 && numbers.length === 1) {
        return {
          title: copy.serverConfirm.deleteSingleQuestion(numbers[0]),
          approveLabel: copy.serverConfirm.deleteApprove,
        };
      }

      if (count > 1) {
        return {
          title: copy.serverConfirm.deleteQuestions(count),
          approveLabel: copy.serverConfirm.deleteApprove,
        };
      }

      return null;
    }
    case 'CONFIRM_REDUCE_COUNT': {
      if (typeof params.target_count !== 'number') return null;

      return {
        title: copy.serverConfirm.reduceCount(params.target_count),
        approveLabel: copy.serverConfirm.reduceApprove,
      };
    }
    case 'CONFIRM_REPLACE_QUESTIONS': {
      if (typeof params.target_count !== 'number') return null;

      return {
        title: copy.serverConfirm.replaceQuestions(params.target_count),
        approveLabel: copy.serverConfirm.replaceApprove,
      };
    }
    case 'CONFIRM_ACTION':
    case undefined:
    case null:
    default:
      // CONFIRM_ACTION(generic)·code 미동봉 legacy — 서버 카피 verbatim.
      return null;
  }
};

const mapConfirmRequiredEvent = (
  event: ServerAiBuilderChatConfirmRequiredEventDto,
  requestId: number,
  copy: AiBuilderChatCopy,
): AiBuilderChatEvent => {
  const approveOption = event.options.find(
    option => option.decision === 'approve',
  );
  const cancelOption = event.options.find(
    option => option.decision === 'cancel',
  );
  const localized = resolveServerConfirmCopy(event, copy);

  return {
    type: 'append',
    message: createConfirmMessage({
      id: createServerAiBuilderChatMessageId({
        event,
        requestId,
        suffix: 'confirm-choice',
      }),
      title: localized?.title ?? event.message,
      traceId: event.trace_id,
      token: event.confirm_token,
      approveLabel:
        localized?.approveLabel ??
        (approveOption?.label || copy.action.defaultApprove),
      cancelLabel: localized
        ? copy.action.defaultCancel
        : cancelOption?.label || copy.action.defaultCancel,
      actionDescription: event.action_description,
      questionRefs: toAiBuilderQuestionRefs(event.question_refs, copy),
    }),
  };
};

const mapRespondEvent = (
  event: ServerAiBuilderChatRespondEventDto,
  requestId: number,
  state: ServerAiBuilderChatEventMapperState,
  copy: AiBuilderChatCopy,
): ServerAiBuilderChatEventMappingResult => {
  const localizedText = resolveServerAssistantCopy(
    event.copy_code,
    event.copy_params,
    copy,
  );
  const displayText = localizedText ?? event.text;
  const responseText = displayText.trim();
  const shouldRenderAssistantMessage =
    responseText.length > 0 || Boolean(state.streamingMessage);
  const pendingProgressMessageId =
    state.progressMessage?.source === 'pending' &&
    responseText.length > 0 &&
    !state.visibleTurnResponse
      ? state.progressMessage.id
      : null;
  const messageId =
    state.streamingMessage?.id ??
    createServerAiBuilderChatMessageId({
      event,
      requestId,
      suffix: 'assistant-response',
    });
  const message = createAssistantMessage({
    id: messageId,
    text: displayText,
    traceId: event.trace_id,
    questionRefs: event.question_refs,
    responseType: event.response_type,
    undoAvailable: event.undo_available,
    serverActions: event.actions,
    copy,
  });
  const shouldReplaceAssistantMessage =
    Boolean(state.streamingMessage) || Boolean(pendingProgressMessageId);
  const assistantEvent: AiBuilderChatEvent | null = shouldRenderAssistantMessage
    ? shouldReplaceAssistantMessage
      ? {
          type: 'replace',
          messageId:
            state.streamingMessage?.id ?? pendingProgressMessageId ?? messageId,
          message,
        }
      : {
          type: 'append',
          message,
        }
    : null;
  const shouldCompleteProgress =
    state.progressMessage?.source === 'server' ||
    (state.progressMessage?.source === 'pending' &&
      !assistantEvent &&
      !state.visibleTurnResponse);
  const terminalProgressEvents =
    shouldCompleteProgress && state.progressMessage
      ? createTerminalProgressEvents({
          progressMessage: state.progressMessage,
          status: 'complete',
          completedQuestionCount: event.question_refs?.length,
          questionUpdate: state.questionUpdate,
          copy,
        })
      : [];

  return {
    events: [
      ...terminalProgressEvents,
      ...(assistantEvent ? [assistantEvent] : []),
      createUsageEvent(event.usage),
    ],
    state: createServerAiBuilderChatEventMapperState(),
  };
};

const mapErrorEvent = (
  event: ServerAiBuilderChatErrorEventDto,
  requestId: number,
  state: ServerAiBuilderChatEventMapperState,
  copy: AiBuilderChatCopy,
): ServerAiBuilderChatEventMappingResult => {
  return {
    events: [
      ...createTerminalProgressEvents({
        progressMessage: state.progressMessage,
        status: 'stopped',
        copy,
      }),
      // 토큰 만료는 오류 말풍선 대신 authExpired 로 흘린다 — 재로그인 모달이
      // 안내를 담당하므로 "일시적인 오류…" 문구가 뜨면 안 된다.
      event.code === 'AUTH_EXPIRED'
        ? createAuthExpiredEvent()
        : createSystemErrorEvent(event, requestId, copy),
      ...(event.usage ? [createUsageEvent(event.usage)] : []),
    ],
    state: createServerAiBuilderChatEventMapperState(),
  };
};

const createAuthExpiredEvent = (): AiBuilderChatStateEvent => {
  return { type: 'authExpired' };
};

const createSessionIdEvent = (sessionId: string): AiBuilderChatStateEvent => {
  return {
    type: 'setSessionId',
    sessionId,
  };
};

const createUsageEvent = (
  usage: ServerAiBuilderChatUsageInfoDto,
): AiBuilderChatStateEvent => {
  return {
    type: 'setUsage',
    usage: toAiBuilderUsageInfo(usage),
  };
};

const toAiBuilderUsageInfo = (
  usage: ServerAiBuilderChatUsageInfoDto,
): AiBuilderUsageInfo => {
  return {
    state: usage.state,
    remaining: usage.remaining,
    limit: usage.limit,
  };
};

const mapQuestionsUpdatedEvent = (
  event: ServerAiBuilderChatQuestionsUpdatedEventDto,
  requestId: number,
  state: ServerAiBuilderChatEventMapperState,
): ServerAiBuilderChatEventMappingResult => {
  return {
    events: [
      {
        type: 'replaceQuestionSnapshots',
        requestId,
        mode: 'replace',
        questions: event.questions.map(toQuestionSnapshot),
        total: event.total,
        changed_question_ids: [...event.changed_question_ids],
        ...(event.moved_question_ids !== undefined
          ? { moved_question_ids: [...event.moved_question_ids] }
          : {}),
        change_type: event.change_type,
        ...(event.generation_id ? { generationId: event.generation_id } : {}),
        ...(event.base_question_list_revision
          ? { baseQuestionListRevision: event.base_question_list_revision }
          : {}),
      },
    ],
    state: {
      ...state,
      questionUpdate: {
        changedCount: event.changed_question_ids.length,
        changeType: event.change_type,
      },
    },
  };
};

const toQuestionSnapshot = (
  question: ServerAiBuilderChatQuestionSnapshotDto,
): AiBuilderChatQuestionSnapshot => {
  return {
    question_id: question.question_id,
    number: question.number,
    quiz_type: question.quiz_type,
    data: question.data,
  };
};

const createQuestionContent = (event: ServerAiBuilderChatQuestionEventDto) => {
  return [event.context, event.question]
    .map(value => value?.trim())
    .filter((value): value is string => Boolean(value))
    .join('\n');
};

const createProgressSteps = (
  previousSteps: AiBuilderProgressStep[],
  event: ServerAiBuilderChatProgressEventDto,
  label: string,
): AiBuilderProgressStep[] => {
  const stepId = event.step_id?.trim() || event.stage;
  const hasCurrentStage = previousSteps.some(step => step.id === stepId);
  // 서버 heartbeat(SSE keepalive 목적, step_id 만 다르고 문구는 동일)가 같은
  // 줄을 반복 추가하지 않도록, 직전 진행 중 스텝과 문구가 같으면 그대로 둔다.
  const lastStep = previousSteps.at(-1);
  if (
    !hasCurrentStage &&
    lastStep?.status === 'running' &&
    lastStep.label === label
  ) {
    return previousSteps;
  }
  const nextSteps = previousSteps.map(step => {
    if (step.id === stepId) {
      return {
        ...step,
        label,
        status: 'running' as const,
      };
    }

    if (step.status === 'running') {
      return {
        ...step,
        status: 'done' as const,
      };
    }

    return step;
  });

  if (!hasCurrentStage) {
    nextSteps.push({
      id: stepId,
      label,
      status: 'running',
    });
  }

  return nextSteps;
};

const createTerminalProgressEvents = ({
  progressMessage,
  status,
  completedQuestionCount,
  questionUpdate,
  copy,
}: {
  progressMessage: ServerAiBuilderChatEventMapperState['progressMessage'];
  status: AiBuilderTerminalProgressStatus;
  completedQuestionCount?: number;
  questionUpdate?: ServerAiBuilderChatEventMapperState['questionUpdate'];
  copy?: AiBuilderChatCopy;
}): AiBuilderChatEvent[] => {
  if (!progressMessage) return [];

  return [
    {
      type: 'replace',
      messageId: progressMessage.id,
      message: createProgressMessage({
        id: progressMessage.id,
        title: createTerminalProgressTitle({
          completedQuestionCount,
          copy,
          questionUpdate,
          status,
          title: progressMessage.title,
        }),
        status,
        steps: progressMessage.steps.map(step => ({
          ...step,
          status:
            status === 'complete'
              ? 'done'
              : step.status !== 'done'
              ? 'stopped'
              : step.status,
        })),
      }),
    },
  ];
};

const createTerminalProgressTitle = ({
  completedQuestionCount,
  copy,
  questionUpdate,
  status,
  title,
}: {
  completedQuestionCount?: number;
  copy?: AiBuilderChatCopy;
  questionUpdate?: ServerAiBuilderChatEventMapperState['questionUpdate'];
  status: AiBuilderTerminalProgressStatus;
  title: string;
}) => {
  if (status !== 'complete') return title;
  if (
    questionUpdate?.changeType === 'create' &&
    questionUpdate.changedCount > 0
  ) {
    return (
      copy?.serverEvent.createdQuestionsProgressTitle(
        questionUpdate.changedCount,
      ) ?? title
    );
  }

  if (completedQuestionCount != null && completedQuestionCount > 0) {
    return (
      copy?.serverEvent.createdQuestionsProgressTitle(completedQuestionCount) ??
      title
    );
  }

  if (questionUpdate?.changeType === 'create') {
    return copy?.serverEvent.questionGenerationComplete ?? title;
  }

  if (questionUpdate && questionUpdate.changedCount > 0) {
    return copy?.serverEvent.changesApplied ?? title;
  }

  return copy?.serverEvent.taskComplete ?? title;
};

const createProgressMessage = ({
  id,
  title,
  status,
  steps,
  thinkingText,
}: {
  id: string;
  title: string;
  status: AiBuilderMappedProgressMessage['status'];
  steps: AiBuilderProgressStep[];
  thinkingText?: string;
}): AiBuilderMappedProgressMessage => {
  return {
    id,
    kind: 'progress',
    title,
    status,
    steps,
    ...(thinkingText ? { thinkingText } : {}),
  };
};

const createChoiceMessage = ({
  id,
  title,
  description,
  missingSlot,
  selectionMode,
  options,
  confirm,
  traceId,
}: {
  id: string;
  title: string;
  description?: string;
  missingSlot?: string;
  selectionMode: AiBuilderMappedChoiceMessage['selectionMode'];
  options: AiBuilderMappedChoiceOption[];
  confirm?: AiBuilderMappedChoiceMessage['confirm'];
  traceId?: string;
}): AiBuilderMappedChoiceMessage => {
  return {
    id,
    kind: 'choice',
    title,
    ...(description ? { description } : {}),
    ...(missingSlot ? { missingSlot } : {}),
    selectionMode,
    options,
    selectedOptionIds: [],
    submitted: false,
    ...(confirm ? { confirm } : {}),
    ...(traceId ? { traceId, feedback: null } : {}),
  };
};

const createConfirmMessage = ({
  actionDescription,
  approveLabel,
  cancelLabel,
  id,
  questionRefs,
  title,
  token,
  traceId,
}: {
  actionDescription?: string;
  approveLabel: string;
  cancelLabel: string;
  id: string;
  questionRefs: AiBuilderMappedConfirmMessage['questionRefs'];
  title: string;
  token: string;
  traceId?: string;
}): AiBuilderMappedConfirmMessage => {
  return {
    id,
    kind: 'confirm',
    title,
    token,
    approveLabel,
    cancelLabel,
    ...(actionDescription ? { actionDescription } : {}),
    ...(questionRefs != null && questionRefs.length !== 0
      ? { questionRefs }
      : {}),
    submitted: false,
    ...(traceId ? { traceId, feedback: null } : {}),
  };
};

const toAiBuilderQuestionRefs = (
  questionRefs: ServerAiBuilderChatQuestionRefDto[] | undefined,
  copy: AiBuilderChatCopy,
): AiBuilderMappedConfirmMessage['questionRefs'] => {
  return questionRefs?.map(questionRef => ({
    questionId: questionRef.question_id,
    quizNumber: questionRef.number,
    label: copy.serverEvent.questionLabel(questionRef.number),
  }));
};

const createAssistantMessage = ({
  id,
  text,
  traceId,
  questionRefs,
  responseType,
  undoAvailable,
  serverActions,
  copy,
}: {
  id: string;
  text: string;
  traceId?: string;
  questionRefs?: ServerAiBuilderChatQuestionRefDto[];
  responseType?: ServerAiBuilderChatRespondResponseType;
  undoAvailable?: boolean;
  serverActions?: ServerAiBuilderChatAssistantActionDto[];
  copy: AiBuilderChatCopy;
}): AiBuilderMappedAssistantMessage => {
  const actions = createAssistantActions({
    copy,
    responseType,
    undoAvailable,
    serverActions,
  });

  return {
    id,
    kind: 'assistant',
    content: text,
    ...(traceId ? { traceId, feedback: null } : {}),
    ...(questionRefs != null && questionRefs.length !== 0
      ? {
          jumpLinks: questionRefs.map(questionRef => ({
            quizNumber: questionRef.number,
            label: copy.serverEvent.questionLabel(questionRef.number),
          })),
        }
      : {}),
    ...(actions ? { actions } : {}),
  };
};

const createAssistantActions = ({
  copy,
  responseType,
  undoAvailable,
  serverActions,
}: {
  copy: AiBuilderChatCopy;
  responseType?: ServerAiBuilderChatRespondResponseType;
  undoAvailable?: boolean;
  serverActions?: ServerAiBuilderChatAssistantActionDto[];
}): AiBuilderMappedAssistantMessage['actions'] => {
  const actions: NonNullable<AiBuilderMappedAssistantMessage['actions']> = [];

  if (undoAvailable) {
    actions.push({
      id: 'undo-last-ai-action',
      label: copy.action.undoLastAiAction,
      type: 'undoLastAiAction',
      prompt: copy.action.undoLastAiAction,
    });
  }

  const mappedServerActions = toAiBuilderAssistantActions(serverActions, copy);
  if (mappedServerActions.length !== 0) {
    actions.push(...mappedServerActions);
  }

  if (responseType === 'generation_complete') {
    actions.push({
      id: 'complete-generation',
      label: copy.action.complete,
      type: 'closePanel',
    });
  }

  return actions.length > 0 ? actions : undefined;
};

const toAiBuilderAssistantActions = (
  serverActions: ServerAiBuilderChatAssistantActionDto[] | undefined,
  copy: AiBuilderChatCopy,
): NonNullable<AiBuilderMappedAssistantMessage['actions']> => {
  if (serverActions == null || serverActions.length === 0) return [];

  const seenIds = new Set<string>();
  return serverActions
    .map((action, index) => {
      const legacyLabel = action.label?.trim();
      const legacyPrompt = action.prompt?.trim();
      if (!legacyLabel || !legacyPrompt) return null;

      const semanticCopy = resolveServerActionCopy(action.action_id, copy);
      const label = semanticCopy?.label ?? legacyLabel;
      const prompt = semanticCopy?.prompt ?? legacyPrompt;
      const emoji = action.emoji?.trim();

      const idBase =
        action.action_id?.trim() ||
        action.id?.trim() ||
        `server-action-${index + 1}`;
      const id = seenIds.has(idBase) ? `${idBase}-${index + 1}` : idBase;
      seenIds.add(id);

      return {
        id,
        label,
        type: 'prompt' as const,
        prompt,
        ...(emoji ? { emoji } : {}),
      };
    })
    .filter((action): action is NonNullable<typeof action> => action !== null);
};

const resolveServerActionCopy = (
  actionId: string | undefined,
  copy: AiBuilderChatCopy,
): { label: string; prompt: string } | null => {
  switch (actionId) {
    case undefined:
      return null;
    case 'source_mismatch_proceed':
      return copy.serverSemantic.action.sourceMismatchProceed;
    case 'source_mismatch_stop':
      return copy.serverSemantic.action.sourceMismatchStop;
    case 'review_answers':
      return copy.serverSemantic.action.reviewAnswers;
    case 'review_source_alignment':
      return copy.serverSemantic.action.reviewSourceAlignment;
    case 'make_easier':
      return copy.serverSemantic.action.makeEasier;
    case 'make_harder':
      return copy.serverSemantic.action.makeHarder;
    case 'add_similar_questions':
      return copy.serverSemantic.action.addSimilarQuestions;
    case 'mix_question_types':
      return copy.serverSemantic.action.mixQuestionTypes;
    case 'add_hints':
      return copy.serverSemantic.action.addHints;
    case 'simplify_wording':
      return copy.serverSemantic.action.simplifyWording;
    case 'align_to_grade':
      return copy.serverSemantic.action.alignToGrade;
    case 'check_duplicates':
      return copy.serverSemantic.action.checkDuplicates;
    case 'fix_problematic_parts':
      return copy.serverSemantic.action.fixProblematicParts;
    default:
      return null;
  }
};

const createServerChipOptionId = (index: number) => {
  return `server-chip-${index + 1}`;
};

const createSystemNoticeEvent = (
  event: ServerAiBuilderChatNoticeEventDto,
  requestId: number,
  copy: AiBuilderChatCopy,
): AiBuilderChatEvent => {
  return {
    type: 'append',
    message: createSystemMessage({
      id: createServerAiBuilderChatMessageId({
        event,
        requestId,
        suffix: `notice-${event.code}`,
      }),
      tone: 'notice',
      // 등록된 code 카피가 있으면 지역화, 없으면 서버 안내문 verbatim.
      content:
        resolveServerAssistantCopy(event.copy_code, event.copy_params, copy) ??
        getServerAiBuilderChatNoticeMessage(event.code, event.message, copy),
    }),
  };
};

const createSystemErrorEvent = (
  event: ServerAiBuilderChatErrorEventDto,
  requestId: number,
  copy: AiBuilderChatCopy,
): AiBuilderChatEvent => {
  return {
    type: 'append',
    message: createSystemMessage({
      id: createServerAiBuilderChatMessageId({
        event,
        requestId,
        suffix: `error-${event.code}`,
      }),
      tone: 'error',
      content: getServerAiBuilderChatErrorMessage(
        event.code,
        copy,
        event.reason,
      ),
      errorCode: event.code,
    }),
  };
};

const createSystemMessage = ({
  id,
  tone,
  content,
  errorCode,
}: {
  id: string;
  tone: AiBuilderMappedSystemMessage['tone'];
  content: string;
  errorCode?: string;
}): AiBuilderMappedSystemMessage => {
  return {
    id,
    kind: 'system',
    tone,
    content,
    ...(errorCode ? { errorCode } : {}),
  };
};

const mapVisibleResponseEvent = (
  event: AiBuilderChatEvent,
  state: ServerAiBuilderChatEventMapperState,
): ServerAiBuilderChatEventMappingResult => {
  const messageEvent =
    state.progressMessage &&
    (event.type === 'append' || event.type === 'replace')
      ? {
          type: 'replace' as const,
          messageId: state.progressMessage.id,
          message: event.message,
        }
      : event;

  return {
    events: [messageEvent],
    state: {
      ...state,
      progressMessage: undefined,
      visibleTurnResponse: true,
    },
  };
};

const getTurnIdentity = (event: ServerAiBuilderChatTurnMetadataDto) => {
  return event.turn_id ?? event.turn;
};

const isSameProgressTurn = (
  progressMessage: ServerAiBuilderChatEventMapperState['progressMessage'],
  event: ServerAiBuilderChatTurnMetadataDto,
) => {
  if (!progressMessage) return false;

  const turnIdentity = getTurnIdentity(event);

  return (
    progressMessage.turnIdentity === undefined ||
    turnIdentity === undefined ||
    progressMessage.turnIdentity === turnIdentity
  );
};

const createServerAiBuilderChatMessageId = ({
  event,
  requestId,
  suffix,
}: {
  event: ServerAiBuilderChatTurnMetadataDto;
  requestId: number;
  suffix: string;
}) => {
  const sessionPart = event.session_id
    ? `session-${sanitizeMessageIdPart(event.session_id)}`
    : null;
  const turnIdentity = event.turn_id ?? event.turn;
  const turnPart =
    turnIdentity !== undefined
      ? `turn-${sanitizeMessageIdPart(turnIdentity)}`
      : 'turn-unknown';
  const messagePart = event.message_id
    ? `message-${sanitizeMessageIdPart(event.message_id)}`
    : null;

  return [
    'server',
    `request-${requestId}`,
    sessionPart,
    turnPart,
    messagePart,
    sanitizeMessageIdPart(suffix),
  ]
    .filter(Boolean)
    .join('-');
};

const sanitizeMessageIdPart = (value: string | number) => {
  return String(value).replace(/[^a-zA-Z0-9_-]+/g, '-');
};
